import { isEqual } from 'lodash';
import {
  getElementQuantityProperties,
  getSelectedElementQuantityProperty,
  isElementQuantityExpressionProperty,
  isElementQuantityName,
  isElementQuantityRecord,
  updateElementQuantityAndPropertiesInRoot,
} from './element-quantity-core.helpers';
import {
  ElementPropertyCountType,
  ElementPropertyResolvedCount,
  ElementPropertyResolvedCounts,
  ElementPropertyType,
  ElementPropertyName,
  ElementPropertyOptionsKey,
  ElementPropertySource,
  ElementPropertySourceID,
  ElementSelectPropertyCountType,
  IElementExpressionProperty,
  IElementProperty,
  IElementPropertyFactoryOption,
  IElementPropertyOption,
  IElementPropertyParent,
  IElementSelectProperty,
  IElementSwitchProperty,
  IFactoryExpressionProperty,
  IFactoryProperty,
  IFactorySelectProperty,
  IFactorySwitchProperty,
  PropertyResolvedCountRecord,
  ElementPropertyTypeMap,
} from '../models/element-property.model';
import {
  elementQuantityNames,
  ElementQuantityPropertyTypeMap,
  ElementQuantityRecord,
  IconQuantityName,
  iconQuantityNames,
  IElementQuantityExpressionProperty,
  IElementQuantityProperty,
} from '../models/element-quantities.model';
import {
  Baseline,
  IElement,
  IID,
  OneOfElementListElements,
  OneOfElements,
  OneOfParentElements,
  OneOfPropertyElements,
  Project,
} from '../models/project.model';
import shallowEqual, {
  EMPTY_FROZEN_ARRAY,
  replaceById,
  findMap,
  getItemByIdOrName,
  isDefined,
  isOneOf,
  removeItems,
} from './array.helpers';
import { getElementCategoryId } from './element-category-id.helpers';
import {
  addElementProperties,
  applyIdAndSourceToProperty,
  createElementProperty,
  createPropertySelectOptions,
  toSwitchCount,
} from './element-property-factory.helpers';
import { createId } from './id.helpers';
import { contentCoverageProperty } from '../templates/categories/common-element-category-properties.model';
import {
  createExpression,
  ExpressionValueFactoryType,
  isExpressionValue,
  isExpressionValueFactoryType,
} from './expression-factory.helpers';
import {
  getExpression,
  isEqualExpressionValues,
} from './expression-solving.helpers';
import {
  ExpressionVariables,
  ExpressionVariablesRecord,
  formatExpressionVariableName,
  omitDefaultVariables,
} from './expression-variables.helpers';
import { clamp, getValueAsNumber, isNumeric } from './math.helpers';
import {
  applyChanges,
  getId,
  hasDefinedProperties,
  isNonArrayObject,
  isPrimitive,
  omit,
  pick,
} from './object.helpers';
import { updateElements } from './project.helpers';
import { getRecipeId, isRecipe, resolveAutoRecipeId } from './recipe.helpers';
import {
  ElementPath,
  flattenElements,
  getBuilding,
  getElementById,
  isBuildingVersionElement,
  isElement,
  isOneOfPropertyElements,
  OneOfSearchableElements,
} from './recursive-element.helpers';
import {
  isValidSelectPropertyCount,
  isValidSwitchPropertyCount,
  validateElementProperties,
  validateElementProperty,
  validatePropertySource,
} from '../validation/element-property.validation';
import {
  ItemOrItemId,
  OptionallyRequired,
  OptionallyRequiredOptions,
  PartialRecord,
  RequireProperties,
} from '../models/type-helpers.model';
import { convertName, findFreeName, labelFromValue } from './string.helpers';
import { cacheFactory, required } from './function.helpers';
import { QuantityUnit } from '../models/unit.model';
import { getSbefPropertyOptions } from '@shared/templates/categories/common-element-category-properties.helpers';
import { ELEMENT_PROPERTY_SELECT_INHERIT_COUNT } from '@shared/constants';
import { guards } from './typeguards.helpers';
import { compare } from './compare.helpers';
import { otherProductOptions } from '@shared/templates/categories/other-product/other-products.helpers';
import { OTHER_PRODUCTS_SELECT_PROPERTY_NAME } from '@shared/templates/categories/other-product/other-product.model';

/**
 * Use this as default properties not cause rerenders of components
 */
const EMPTY_PROPERTIES: Readonly<Readonly<IElementProperty>[]> = Object.freeze(
  [],
);

const ELEMENT_PROPERTY_INPUT_TYPES = Object.values(ElementPropertyType);
const elementPropertyTypeSet = new Set<string>(ELEMENT_PROPERTY_INPUT_TYPES);

export type IPropertiesOrPropertyParent =
  IElementProperty[] | IElementPropertyParent | ElementQuantityRecord;

type ElementWithProperties = Pick<OneOfPropertyElements, 'properties'>;

// Require name and id to be able to update a property (but keep smartness around different types of count)
export type PropertyChange =
  | RequireProperties<IFactoryExpressionProperty, 'id'>
  | RequireProperties<IFactorySelectProperty, 'id'>
  | RequireProperties<IFactorySwitchProperty, 'id'>
  | RequireProperties<IFactoryExpressionProperty, 'name'>
  | RequireProperties<IFactorySelectProperty, 'name'>
  | RequireProperties<IFactorySwitchProperty, 'name'>;

type PropertyChangeRecord = {
  [name: string]: Partial<PropertyChange> | PropertyChange['count'] | undefined;
};

/**
 * Convert a property change record to an array of property changes
 * @param recordOrArray
 * @returns
 */
export const propertyChangeRecordToArray = (
  recordOrArray: PropertyChangeRecord | (PropertyChange | undefined)[] = [],
): PropertyChange[] => {
  return Array.isArray(recordOrArray)
    ? recordOrArray.filter(isDefined)
    : Object.entries(recordOrArray)
        .map(([name, change]) => {
          if (change === undefined) {
            return;
          }
          if (isPrimitive(change)) {
            return { name, count: change } as PropertyChange;
          }
          return { name, ...change } as PropertyChange;
        })
        .filter(isDefined);
};

/** Options for {@link applyElementPropertyChanges}. */
export type ApplyElementPropertyChangesOptions = {
  /**
   * Skip {@link validateElementProperty} when merging changes (e.g. category-driving
   * preview in {@link propertyChangesToProjectChanges}). Stored select `count` can
   * lag freshly resolved `options` until category enrichment runs.
   */
  skipValidation?: boolean;
};

const isApplyElementPropertyChangesOptions = (
  value: unknown,
): value is ApplyElementPropertyChangesOptions =>
  guards.is.object(value) &&
  !Array.isArray(value) &&
  ('skipValidation' in value || Object.keys(value as object).length === 0) &&
  !('name' in value) &&
  !('id' in value);

/**
 * Apply element property changes to a list of properties
 * NOTE: Broken out unmodifed from setElementProperties...
 * @param properties
 * @param optionsOrChange - Optional {@link ApplyElementPropertyChangesOptions} before changes
 * @param propertyChanges
 * @returns
 */
export function applyElementPropertyChanges<
  T extends IElementProperty,
  C extends PropertyChange,
>(
  properties: T[],
  optionsOrChange: ApplyElementPropertyChangesOptions | C | undefined,
  ...rest: (C | undefined)[]
): T[];
export function applyElementPropertyChanges<
  T extends IElementProperty,
  C extends PropertyChange,
>(properties: T[], ...propertyChanges: (C | undefined)[]): T[];
export function applyElementPropertyChanges<
  T extends IElementProperty,
  C extends PropertyChange,
>(
  properties: T[],
  optionsOrChange: ApplyElementPropertyChangesOptions | C | undefined,
  ...rest: (C | undefined)[]
): T[] {
  let options: ApplyElementPropertyChangesOptions = {};
  let propertyChanges: (C | undefined)[];
  if (isApplyElementPropertyChangesOptions(optionsOrChange)) {
    options = optionsOrChange;
    propertyChanges = rest;
  } else {
    propertyChanges = [optionsOrChange, ...rest];
  }

  const changes = propertyChanges.filter(guards.is.defined);
  if (!changes.length) {
    return properties;
  }

  const updatedProperties = properties.map((property) => {
    const change = getItemByIdOrName(changes, property, false);
    if (!change) {
      return property;
    }
    // Remove item from changes so we can validate that we made all changes
    removeItems(changes, change);

    return createElementProperty(
      { ...property, ...omit(change, 'id', 'type') }, // Can't change id or type
      undefined,
      false,
      { skipValidation: options.skipValidation },
    );
  });

  if (changes.length) {
    throw new Error('Could not find all properties to update');
  }

  // Update element if properties have changed else return project unmodified
  if (!compare.equal.shallow(updatedProperties, properties)) {
    return updatedProperties as T[];
  }
  return properties;
}

/**
 * Update element properties in an element with one or more changes.
 * Note. Passing undefined properties will update the property with an undefined value.
 * @param root A building version or project that will be returned updated
 * @param elementChanges
 * @returns
 */
export const setElementProperties = <
  R extends OneOfParentElements | Project,
  T extends PropertyChange,
>(
  root: R,
  elementOrId: ItemOrItemId<OneOfPropertyElements>,
  ...propertyChanges: (T | undefined)[]
): R => {
  const elementId = getId(elementOrId);

  // Make sure to fetch the latest instance to not overwrite any changes made
  const element = getElementById(root, elementId);
  const properties = getElementProperties(element);

  if (!isOneOfPropertyElements(element)) {
    throw new Error(`Element not found with id ${elementId}`);
  }

  // Apply the changes to the properties (if there are changes)
  const updatedProperties = applyElementPropertyChanges(
    properties,
    ...propertyChanges,
  );

  // Update element if properties have changed else return project unmodified
  if (updatedProperties !== properties) {
    return updateElements(root, {
      id: elementId,
      properties: updatedProperties,
    } as IElement);
  }
  return root;
};

/**
 * Update all element properties and quantity properties in a version based on the VariablesRecord
 * @param root A building version or project that will be returned updated
 * @param record Record ({ [element.id]: variables }) with everything to update
 * @param scope Elements to update. Provide an element further down the hierarchy to update only that element and not its parents. Defaults to the root.
 * @returns Modified project
 */
export const updateElementPropertiesFromVariablesRecord = <
  R extends Baseline | Project,
>(
  root: R,
  record: ExpressionVariablesRecord,
  scope: R | OneOfParentElements = root,
): R => {
  // Elements to update
  const ids = flattenElements(scope).map((e) => e.id);
  for (const id of ids) {
    const variables = record[id];
    if (!variables) {
      console.warn(`No variables found for element ${id}`);
    } else {
      root = updateElementPropertiesFromVariables(root, id, variables);
    }
  }
  return root;
};

/**
 * Update all element properties and quantity properties in a version based on the VariablesRecord
 * @param project
 * @param version
 * @param record Record ({ [element.id]: variables }) with everything to update. Smaller record = less computation
 * @returns Modified project
 */
export const updateElementPropertiesFromVariables = <
  R extends Baseline | Project,
>(
  root: R,
  elementOrId: ItemOrItemId<OneOfElements>,
  variables: ExpressionVariables,
): R => {
  const nonDefaultKeys = Object.keys(omitDefaultVariables(variables));
  // Version & Element will be a new version as soon as any property is updated (since project is immutable)
  const element =
    typeof elementOrId === 'string'
      ? getElementById(root, elementOrId, true)
      : elementOrId;
  const properties = getElementAndQuantityProperties(element);

  if (
    !isOneOfPropertyElements(element) ||
    !properties.length ||
    !nonDefaultKeys.length
  ) {
    return root;
  }

  const parentVariables = guards.is.object(variables.parent)
    ? (variables.parent as ExpressionVariables)
    : undefined;

  const changes: PropertyChange[] = properties
    .map((property) => {
      const key = formatExpressionVariableName(property.name);
      // inheritFallback without a user count follows the parent; a local count
      // must keep its own resolved value (same rule as expression variables).
      const value =
        property.inheritFallback && !hasCount(property)
          ? parentVariables?.[key]
          : variables[key];

      // Base for any update
      const base = pick(property, 'id', 'name');

      // Skip when there is no parent value — keeps stale inherit fallback (DEV-3354)
      if (value === undefined) {
        return;
      }
      const countKey = hasCount(property) ? 'count' : 'fallbackCount';

      if (isElementExpressionProperty(property)) {
        // Get either count or fallbackCount
        const count = getCount(property);
        // value override takes precedence over expression resolution
        const resolved = count.value ?? (guards.is.number(value) ? value : 0);

        // Only update if resolved value has changed since expression is not updated by this
        if (count.resolved === resolved) {
          return;
        }

        return {
          ...base,
          [countKey]: createExpression({
            value: count.value,
            resolved,
            expression: count.expression,
          }),
        };
      } else if (isElementSelectProperty(property)) {
        let fallbackCount = isInheritableFallbackCount(property, value)
          ? (value as IElementSelectProperty['count'])
          : undefined;

        // If count is defined or inherited values is not defined in options we don't need to update
        if (!isValidSelectPropertyCount(fallbackCount)) {
          return;
        }
        const change: PropertyChange = {
          ...base,
          type: ElementPropertyType.Select,
          fallbackCount,
          inheritFallback: true,
        };
        return change;
      } else if (isElementSwitchProperty(property)) {
        if (
          !isInheritableFallbackCount(property, value) ||
          !isValidSwitchPropertyCount(value)
        ) {
          return;
        }
        const change: PropertyChange = {
          ...base,
          type: ElementPropertyType.Switch, // Just for typing
          fallbackCount: value,
          inheritFallback: true,
        };
        return change;
      }
      return undefined;
    })
    .filter(isDefined);

  return updateElementQuantityAndPropertiesInRoot(root, element, ...changes);
};

/**
 * Refresh `count` / `fallbackCount` `resolved` from a variables bag (same rules as
 * {@link updateElementPropertiesFromVariables}). Used when proposal recipe snapshots
 * still carry stale resolved numbers after {@link autoUpdateElementQuantity} (DEV-3347).
 *
 * A numeric override wins over the formula, same as `solve`. A proposal action
 * writes only `count.value`, so `resolved` can stay on the number from before the edit
 * (DEV-3662).
 */
export const syncElementExpressionResolvedFromVariables = (
  element: IElement,
  variables: ExpressionVariables,
): IElement => {
  const properties = getElementAndQuantityProperties(element);
  if (!properties.length) return element;

  let quantity = element.quantity;
  let touched = false;

  for (const property of properties) {
    if (!isElementExpressionProperty(property)) continue;

    const key = formatExpressionVariableName(property.name);
    const count = getCount(property);
    // Typed amount wins. Otherwise take the solved symbol for this row.
    const fromVariables = variables[key];
    const overrideValue = guards.is.number(count.value)
      ? count.value
      : undefined;
    const variableValue = guards.is.number(fromVariables)
      ? fromVariables
      : undefined;
    const resolved = overrideValue ?? variableValue;
    if (!guards.is.number(resolved)) continue;
    if (count.resolved === resolved) continue;

    const countKey = hasCount(property) ? 'count' : 'fallbackCount';
    const row = quantity?.[property.name as keyof ElementQuantityRecord];
    if (!row || !isElementQuantityExpressionProperty(row)) continue;

    quantity = {
      ...quantity,
      [property.name]: {
        ...row,
        [countKey]: createExpression({
          value: count.value,
          resolved,
          expression: count.expression,
        }),
      },
    };
    touched = true;
  }

  return touched && quantity ? { ...element, quantity } : element;
};

/**
 * Test if a property supports inheritance
 * @param property
 * @returns
 */
export const isInheritanceSupported = (property: IElementProperty): boolean =>
  property?.inheritFallback === true;

/**
 * Whether `count` may be cleared: something must take over, either a
 * {@link IElementProperty.fallbackCount} or inheritance ({@link isInheritanceSupported}).
 * {@link setPropertyCount} throws when clearing a count that fails this check.
 *
 * @param property - Property whose count would be cleared
 * @returns True when clearing `count` leaves the property with a value
 */
export const isPropertyCountClearable = (
  property: IElementProperty | IElementQuantityProperty,
): boolean => hasFallbackCount(property) || isInheritanceSupported(property);

/**
 * Test if a fallback count (value) is ok to for the property to inherit
 * @param property
 * @param newFallback New fallback count value
 * @returns
 */
export const isInheritableFallbackCount = (
  property: IElementProperty,
  newFallback: unknown,
): boolean => {
  // Cases where fallback count is not inheritable
  if (
    !property || // Property must be defined
    hasCount(property) || // Do not inherit fallback count if count is set
    newFallback === undefined || // Undefined fallback count is not inheritable
    !isInheritanceSupported(property) // Property must be set to inherit fallback
  ) {
    return false;
  }
  // Select properties can only inherit fallback count if the new fallback is one of the options
  if (
    isElementSelectProperty(property) &&
    !isSelectPropertyOptionValue(property, newFallback)
  ) {
    return false;
  }

  // Expression properties can only inherit fallback count if the new fallback is an ExpressionValue
  if (
    isElementExpressionProperty(property) &&
    !isExpressionValue(newFallback)
  ) {
    return false;
  }

  // Switch properties can only inherit fallback count if the new fallback is boolean
  if (
    isElementSwitchProperty(property) &&
    !isValidSwitchPropertyCount(newFallback)
  ) {
    return false;
  }
  return true;
};

/**
 * Check if user has selected to inherit count from parent.
 * @param property Property that should be checked if inheritance is selected
 * @param newCount The new count user have selected / entered
 * @returns
 */
export const isFallbackInheritanceSelected = <T extends IElementProperty>(
  property: T,
  newCount: T['count'] | undefined,
): boolean => {
  // If inheritance is not supported, user can never select to inherit
  if (!isInheritanceSupported(property)) {
    return false;
  }
  // Undefined count always means user has selected to inherit
  if (guards.is.undefined(newCount)) {
    return true;
  }

  // Select properties can be inherited if user selects "auto"
  if (
    isElementSelectProperty(property) &&
    ELEMENT_PROPERTY_SELECT_INHERIT_COUNT === newCount
  ) {
    // Make sure inheritance options is not added to options
    if (
      isSelectPropertyOptionValue(
        property,
        ELEMENT_PROPERTY_SELECT_INHERIT_COUNT,
      )
    ) {
      console.error(
        `Inheritable property "${property.name}" have forbidden option "${ELEMENT_PROPERTY_SELECT_INHERIT_COUNT}, hence inheritance will not be applied for safety"`,
      );
      return false;
    }

    return true;
  }

  // Expressions can be left blank to inherit
  if (
    isElementExpressionProperty(property) &&
    isExpressionValue(newCount) &&
    newCount.expression === ''
  ) {
    return false;
  }

  return false;
};

/**
 * Update the count of an element property by name or id.
 *
 * How {@link count} is interpreted for expression properties:
 * - `number` — creates a new count with `.expression` set to the stringified number.
 *   Does **not** set `.value`. Use an {@link ExpressionValue} object to set `.value`.
 * - `string` — creates a new count with `.expression` set to the string.
 * - {@link ExpressionValue} — used as-is (cloned). Pass `.value` to set an explicit override.
 * - `Partial<ExpressionValue>` — merged into a new count. `.value` is preserved (even if `undefined`).
 * - `undefined` — removes the count (only valid when a `fallbackCount` exists or
 *   {@link isInheritanceSupported} is true).
 *
 * @example
 * // Set .value as an explicit user override, preserving any existing expression:
 * setPropertyCountByNameOrId(element, 'Lifetime', { ...existingCount, value: 50 })
 *
 * // Remove .value override, keeping the expression:
 * setPropertyCountByNameOrId(element, 'Lifetime', { ...existingCount, value: undefined })
 *
 * // Set expression (no .value — resolved is derived from the expression):
 * setPropertyCountByNameOrId(element, 'Lifetime', 'GFA * 0.5')
 *
 * // Remove count entirely (falls back to fallbackCount):
 * setPropertyCountByNameOrId(element, 'Lifetime', undefined)
 */
export const setPropertyCountByNameOrId = <
  T extends IPropertiesOrPropertyParent,
>(
  elementOrProps: T,
  nameOrId: string,
  count: ExpressionValueFactoryType | ElementPropertyCountType | undefined,
): T => {
  const current = getElementProperty(elementOrProps, nameOrId, true);
  const updated = setPropertyCount(current, count);

  return replaceElementProperties(elementOrProps, updated as typeof current);
};

/**
 * Set multiple properties count values from a record of properties (name => count)
 * @param elementOrProps
 * @param countRecord
 * @param ignoreMissingProperties If true, properties that are not found will be ignored. If false, an error will be thrown.
 * @returns
 */
export const setPropertyCountsFromRecord = <
  T extends IPropertiesOrPropertyParent,
>(
  elementOrProps: T,
  countRecord?: PartialRecord<
    string,
    ExpressionValueFactoryType | ElementPropertyCountType | undefined
  >,
  ignoreMissingProperties = false,
): T => {
  for (const [name, count] of Object.entries(countRecord ?? {})) {
    if (ignoreMissingProperties && !getElementProperty(elementOrProps, name)) {
      continue;
    }
    elementOrProps = setPropertyCountByNameOrId(elementOrProps, name, count);
  }
  return elementOrProps;
};

export type SetPropertyCountMap = {
  [ElementPropertyType.Expression]: ExpressionValueFactoryType;
  [ElementPropertyType.Select]: IElementSelectProperty['count'];
  [ElementPropertyType.Switch]: IElementSwitchProperty['count'];
};

type SetPropertyCount<
  T extends keyof SetPropertyCountMap | undefined = undefined,
> = T extends string
  ? SetPropertyCountMap[T]
  : SetPropertyCountMap[ElementPropertyType.Expression];

/**
 * Options for {@link setPropertyCount}.
 *
 * `skipValidation` lets pipeline transforms bypass {@link validateElementProperty}.
 * Whole-property validation includes the select `count`-in-`options` check, which can
 * legitimately fail mid-pipeline when a category-driving edit on a proposal arrives
 * before category enrichment refreshes the options list. Validation belongs at the
 * input boundary (and post-enrichment), not inside data transforms.
 *
 * Type-level checks (e.g. {@link isValidSelectPropertyCount}) and the
 * "undefined → fallback" safety net always run, regardless of this flag.
 */
export type SetPropertyCountOptions = {
  skipValidation?: boolean;
};

/**
 * Set count value of a property
 * @param property Property to modify. If not changed
 * @param count New count value. For expression values count can be provided as a number or a string.
 * @param type Whether to set `count` (default) or `fallbackCount`.
 * @param options See {@link SetPropertyCountOptions}.
 * @returns
 */
export const setPropertyCount = <
  T extends IElementProperty | IElementQuantityProperty,
  D extends SetPropertyCount<T['type']>,
  R = D extends ElementPropertyCountType ? RequireProperties<T, 'count'> : T,
>(
  property: T,
  count: D | undefined,
  type: CountType = 'count',
  options: SetPropertyCountOptions = {},
): R => {
  if (count === undefined && type === 'count') {
    if (!isPropertyCountClearable(property)) {
      throw new Error(
        'Cannot set count to undefined for property without fallback count or inheritFallback',
      );
    }

    // Remove count if it exist else leave property unmodified
    if (!hasCount(property)) return property as unknown as R;
    return { ...property, count: undefined } as R;
  }

  if (isElementExpressionProperty(property)) {
    return setExpressionPropertyCount(
      property,
      count as SetPropertyCount<ElementPropertyType.Expression>,
      type,
      options,
    ) as R;
  } else if (isElementSelectProperty(property)) {
    return setSelectPropertyCount(
      property,
      count as SetPropertyCount<ElementPropertyType.Select>,
      type,
      options,
    ) as R;
  } else if (isElementSwitchProperty(property)) {
    return setSwitchPropertyCount(
      property,
      count as SetPropertyCount<ElementPropertyType.Switch>,
      type,
      options,
    ) as R;
  }
  throw new Error('Unknown property type');
};

/**
 * Either runs {@link validateElementProperty} or returns the property unchanged when
 * {@link SetPropertyCountOptions.skipValidation} is `true`.
 */
const validateOrPassthrough = <T extends IElementProperty>(
  property: T,
  options: SetPropertyCountOptions,
): T => (options.skipValidation ? property : validateElementProperty(property));

const setExpressionPropertyCount = (
  property: IElementExpressionProperty,
  countFactory:
    | ExpressionValueFactoryType
    | IElementExpressionProperty['count']
    | IElementExpressionProperty['fallbackCount'],
  type: CountType = 'count',
  options: SetPropertyCountOptions = {},
): IElementExpressionProperty => {
  const allowUndefined = type === 'fallbackCount';

  // Can even count be 'undefined'? We allow it for fallbackCount
  if (
    countFactory === 'undefined' ||
    (allowUndefined && countFactory === undefined)
  ) {
    return validateOrPassthrough({ ...property, [type]: undefined }, options);
  }

  if (!isExpressionValueFactoryType(countFactory)) {
    throw new Error('Cannot set count to an invalid expression value');
  }

  // Make sure count is an expression
  const count = getExpression(countFactory);

  // Update if count is changed
  if (!isEqualExpressionValues(count, property[type], true)) {
    return validateOrPassthrough(
      { ...property, [type]: getExpression(count) },
      options,
    );
  }
  return property;
};

type CountType = 'count' | 'fallbackCount';

const setSelectPropertyCount = (
  property: IElementSelectProperty,
  newCount: IElementProperty['count'] | IElementSelectProperty['fallbackCount'],
  type: CountType = 'count',
  options: SetPropertyCountOptions = {},
): IElementSelectProperty => {
  const allowUndefined = type === 'fallbackCount';
  if (!isValidSelectPropertyCount(newCount, allowUndefined)) {
    throw new Error('Cannot set count to an invalid select value');
  }
  if (newCount !== property[type]) {
    return validateOrPassthrough({ ...property, [type]: newCount }, options);
  }
  return property;
};

const setSwitchPropertyCount = (
  property: IElementSwitchProperty,
  newCount: IElementProperty['count'] | IElementSwitchProperty['fallbackCount'],
  type: CountType = 'count',
  options: SetPropertyCountOptions = {},
): IElementSwitchProperty => {
  const allowUndefined = type === 'fallbackCount';
  if (!isValidSwitchPropertyCount(newCount, allowUndefined)) {
    throw new Error('Cannot set count to an invalid switch value');
  }
  if (newCount !== property[type]) {
    return validateOrPassthrough({ ...property, [type]: newCount }, options);
  }
  return property;
};

/**
 * Require the element property to be of a specific type (Expression, Select, Switch)
 * @param element
 * @param kinds Pass in multiple kinds to allow for multiple kinds
 * @returns
 */
export const requirePropertyOfType = <
  T extends IElementProperty | IElementQuantityProperty,
  M extends (T extends IElementQuantityProperty
    ? ElementQuantityPropertyTypeMap
    : ElementPropertyTypeMap),
  K extends keyof M,
>(
  property: T | undefined,
  ...types: K[]
): M[K] => {
  const propertyType = property ? getElementPropertyType(property) : undefined;

  if (!types.length) {
    throw new Error('No types provided');
  }

  if (!isOneOf(types, propertyType)) {
    throw new Error(
      `Element property type mismatch. Expected ${types.join(' or ')} but got ${propertyType}`,
    );
  }
  return property as M[K];
};

/**
 * Get count OR fallbackCount from property or element. For IElement return the selected quantity property count.
 * @param propertyOrElement
 * @returns
 */
export const getCount = <
  T extends IElementProperty | OneOfElements,
  C extends (T extends IElement
    ? IElementQuantityExpressionProperty['count' | 'fallbackCount']
    : Exclude<T, IElement | Baseline>['count']),
  R extends boolean = true,
>(
  propertyOrElement: T | undefined,
  requireCount: R = true as R,
): OptionallyRequired<C, R> => {
  // Just to make it easier to use everywhere without typechecking too much
  if (isBuildingVersionElement(propertyOrElement)) {
    return required(
      undefined,
      requireCount ? 'Building version has no count' : false,
    ) as OptionallyRequired<C, R>;
  }

  // If an element is provided, get the selected quantity property and rerun this function
  if (isElement(propertyOrElement)) {
    return getCount(
      getSelectedElementQuantityProperty(propertyOrElement),
      requireCount,
    ) as OptionallyRequired<C, R>;
  }

  return required(
    propertyOrElement?.count ?? propertyOrElement?.fallbackCount,
    requireCount
      ? `Property  "${propertyOrElement?.name}" has no count`
      : false,
  );
};

/**
 * Get unit from property or element. For IElement return the unit of the selected quantity property.
 * @param propertyOrElement
 * @returns
 */
export const getUnit = <
  T extends IElementProperty | OneOfElements,
  R extends boolean = true,
>(
  propertyOrElement: T | undefined,
  requireUnit: R = true as R,
): OptionallyRequired<QuantityUnit, R> => {
  // If an element is provided, get the selected quantity property and rerun this function
  if (isElement(propertyOrElement)) {
    return getUnit(
      getSelectedElementQuantityProperty(propertyOrElement),
      requireUnit,
    ) as OptionallyRequired<QuantityUnit, R>;
  }

  return required(
    !!propertyOrElement && 'unit' in propertyOrElement
      ? propertyOrElement.unit
      : undefined,
    requireUnit ? `Property "${propertyOrElement?.name}" has no unit` : false,
  );
};

export const hasCount = <
  T extends IElementProperty | Partial<IFactoryProperty>,
>(
  prop?: T | RequireProperties<T, 'count'>,
): prop is RequireProperties<T, 'count'> => {
  return prop?.count !== undefined;
};

export const hasFallbackCount = <
  T extends IElementProperty | Partial<IFactoryProperty>,
>(
  prop?: T | RequireProperties<T, 'fallbackCount'>,
): prop is RequireProperties<T, 'fallbackCount'> => {
  return prop?.fallbackCount !== undefined;
};

/**
 * Return value from property.count. If count is an expression, return the resolved value.
 * @param property
 * @returns
 */
export const getElementPropertyResolvedCount = <
  T extends IElementProperty | IFactoryProperty,
  D extends ElementPropertyResolvedCount<T['type']> | undefined,
>(
  property: T | undefined,
  defaultValue?: D | undefined,
): D => {
  if (!property) {
    return defaultValue as D;
  }
  const { type } = property;

  // Expression type
  if (!type || type === ElementPropertyType.Expression) {
    // Typecheck needed for TS to know defaultValue is correct type
    if (defaultValue !== undefined && typeof defaultValue !== 'number') {
      throw new Error('Default value must be number');
    }

    return getExpressionPropertyResolvedCount(property, defaultValue) as D;
  }
  // Select type
  else if (type === ElementPropertyType.Select) {
    // Typecheck needed for TS to know defaultValue is correct type
    if (
      defaultValue !== undefined &&
      typeof defaultValue !== 'number' &&
      typeof defaultValue !== 'string'
    ) {
      throw new Error('Default value must be number for select');
    }

    return getSelectPropertyResolvedCount(property, defaultValue) as D;
  }
  // Switch type
  else if (type === ElementPropertyType.Switch) {
    // Typecheck needed for TS to know defaultValue is correct type
    if (defaultValue !== undefined && typeof defaultValue !== 'boolean') {
      throw new Error('Default value must be a boolean for switch type');
    }

    return getSwitchPropertyResolvedCount(property, defaultValue) as D;
  }
  throw new Error('Property type not supported');
};

const getExpressionPropertyResolvedCount = (
  property?: IElementExpressionProperty | IFactoryExpressionProperty,
  defaultValue = 0,
): number => {
  const count = property?.count ?? property?.fallbackCount;
  if (isExpressionValue(count)) {
    // User override (`value`) wins until pipeline refreshes `resolved` (e.g. proposal quantity edits).
    return count.value ?? count.resolved ?? defaultValue;
  }
  // Factory count may be a partial ExpressionValue (not passing isExpressionValue)
  if (guards.is.object(count)) {
    return createExpression(count as ExpressionValueFactoryType).resolved;
  }
  return getValueAsNumber(count, defaultValue);
};

/**
 * Get a select property count value. If count is undefined, return the first option value.
 * Will crash if no value can be returned
 * @param property
 * @param defaultValue
 * @returns
 */
export const getSelectPropertyResolvedCount = (
  property: IElementSelectProperty | IFactorySelectProperty,
  defaultValue?: ElementSelectPropertyCountType,
): ElementSelectPropertyCountType | undefined => {
  const { count, fallbackCount, inheritFallback } = property;

  const option = getSelectPropertyOptions(property)[0];
  const optionValue = option && getSelectPropertyOptionValue(option);

  // Get the first defined value in this order
  const value = [count, fallbackCount, defaultValue, optionValue]
    .filter(isDefined)
    .filter(isPrimitive)[0];

  if (value === undefined && !inheritFallback) {
    throw new Error('Select property count value is undefined');
  }
  return value;
};

/**
 * Test if a value is a valid option value for a select property.
 * I.E. That it's a string or a number that is included in the options array
 * @param prop
 * @param count
 * @returns
 */
export const isSelectPropertyOptionValue = (
  prop: IElementSelectProperty | IFactorySelectProperty,
  count: unknown,
): count is ElementSelectPropertyCountType => {
  if (
    typeof count === 'string' ||
    (typeof count === 'number' && isFinite(count))
  ) {
    const options =
      getSelectPropertyOptions(prop).map(getSelectPropertyOptionValue) ?? [];
    return options.includes(count);
  }
  return false;
};

const getSelectPropertyOptionsFromKey = (
  key: ElementPropertyOptionsKey,
): IElementPropertyOption[] =>
  // Cache to avoid recreating 100s of options for every element (and rerendering of components)
  cacheFactory(() => {
    switch (key) {
      case ElementPropertyName.SbefCode:
        return createPropertySelectOptions(getSbefPropertyOptions());
      case OTHER_PRODUCTS_SELECT_PROPERTY_NAME:
        return [...otherProductOptions];
      default:
        return [];
    }
  }, `getSelectPropertyOptionsFromKey["${key}"]`);

/**
 * Get options from a select property or an array of options or a key.
 * Use this to not have to store options in the property itself.
 * @param propOrOptions
 * @returns
 */
export const getSelectPropertyOptions = (
  prop: IElementSelectProperty | IFactorySelectProperty,
): IElementPropertyOption[] => {
  const options = prop.options;

  if (!options) {
    return EMPTY_FROZEN_ARRAY as IElementPropertyOption[];
  }

  if (typeof options === 'string') {
    return getSelectPropertyOptionsFromKey(options);
  }

  if (!guards.is.array(options)) {
    return [];
  }

  // Already valid options, don't make a new array
  if (options.every(isSelectPropertyOption)) {
    return options as IElementPropertyOption[];
  }

  // Create options from factory options
  return createPropertySelectOptions(options);
};

const isSelectPropertyOption = (
  option: IElementPropertyOption | IElementPropertyFactoryOption,
): option is IElementPropertyOption => {
  return (
    isNonArrayObject(option) && 'value' in option && isPrimitive(option.value)
  );
};

/**
 * Get a select property count value. If count is undefined, return the first option value.
 * Will crash if no value can be returned
 * @param property
 * @param defaultValue
 * @returns
 */
export const getSwitchPropertyResolvedCount = (
  { count, fallbackCount }: IElementSwitchProperty | IFactorySwitchProperty,
  defaultValue = false,
): boolean => toSwitchCount(count ?? fallbackCount) ?? defaultValue;

/**
 * Get value from select property option. If option is an object, return the value property.
 * @param option
 * @returns
 */
export const getSelectPropertyOptionValue = (
  option: IElementPropertyFactoryOption,
): ElementSelectPropertyCountType =>
  typeof option !== 'object' ? option : option.value;

/**
 * Get a property from a list of properties (or element) by name or id
 * @param elementOrProps
 * @param nameOrId
 * @param throwIfNotFound If true, will throw an error if property is not found
 * @returns
 */
export function getElementProperty<
  T extends IPropertiesOrPropertyParent | IElementProperty,
  E extends OptionallyRequiredOptions = false,
  R = OptionallyRequired<IElementProperty, E>,
>(
  elementOrProps: T | undefined,
  nameOrId?: IElementProperty['name'],
  throwIfNotFound: E = false as E,
): R {
  if (isElementProperty(elementOrProps)) {
    return elementOrProps as R;
  }
  const properties = getElementProperties(elementOrProps);
  return getItemByIdOrName(
    properties,
    nameOrId as string,
    throwIfNotFound
      ? `Property with name ${nameOrId as string} not found`
      : false,
  ) as R;
}

type GetResolvedCountDefault =
  ElementPropertyResolvedCounts | IFactoryProperty[] | undefined;
type GetResolvedCountReturnValue<D extends GetResolvedCountDefault> =
  D extends undefined
    ? ElementPropertyResolvedCounts | undefined
    : D extends IFactoryProperty[]
      ? ElementPropertyResolvedCounts
      : D;

/**
 * Get a property count value (primitive) from a list of properties or element by name
 * @param elementOrProps
 * @param nameOrId
 * @param defaultValue Either a fixed value or a list of properties that must contain a property with the same name as requested
 * @returns
 */
export const getElementPropertyResolvedCountByNameOrId = <
  D extends GetResolvedCountDefault,
  R = GetResolvedCountReturnValue<D>,
>(
  elementOrProps: IPropertiesOrPropertyParent | undefined,
  nameOrId: IElementProperty['name'],
  defaultValue?: D,
): R => {
  const property = getElementProperty(elementOrProps, nameOrId);
  // Support to pass a list of properties as default value
  if (Array.isArray(defaultValue)) {
    const defaultValueFromArray = getElementPropertyResolvedCount(
      getItemByIdOrName(
        defaultValue,
        nameOrId,
        `No default value found for property ${nameOrId}`,
      ),
    );
    return getElementPropertyResolvedCount(
      property,
      defaultValueFromArray,
    ) as R;
  }

  return getElementPropertyResolvedCount(property, defaultValue) as R;
};

export const getElementPropertyResolvedCountRecord = (
  elementOrProps: IPropertiesOrPropertyParent | undefined,
): PropertyResolvedCountRecord =>
  getElementProperties(elementOrProps).reduce((acc, property) => {
    return {
      ...acc,
      [property.name]: getElementPropertyResolvedCount(property),
    };
  }, {});

export const getElementPropertyType = (
  prop: IElementProperty | IFactoryProperty,
): ElementPropertyType => {
  return prop?.type ?? ElementPropertyType.Expression;
};

/**
 * Test if a property is an element property
 * @param prop
 * @returns
 */
export const isElementProperty = (prop: unknown): prop is IElementProperty => {
  // Cast once to avoid TS errors
  const propObj = prop as IElementExpressionProperty;

  return (
    isNonArrayObject(propObj) &&
    hasDefinedProperties(propObj, 'type', 'name') &&
    isElementPropertyInputType(propObj.type)
  );
};

const isElementPropertyInputType = (
  type: unknown,
): type is ElementPropertyType => {
  return typeof type === 'string' && elementPropertyTypeSet.has(type);
};

export const isElementExpressionProperty = (
  prop: IElementProperty | undefined,
): prop is IElementExpressionProperty =>
  !!prop && getElementPropertyType(prop) === ElementPropertyType.Expression;

export const isElementSelectProperty = (
  prop: unknown,
): prop is IElementSelectProperty =>
  isNonArrayObject(prop) &&
  'type' in prop &&
  prop.type === ElementPropertyType.Select;

export const isElementSwitchProperty = (
  prop: IElementProperty | undefined,
): prop is IElementSwitchProperty => prop?.type === ElementPropertyType.Switch;

/***
 * Test if two properties are equal
 */
export const isElementPropertiesEqual = (
  propA: IElementProperty,
  propB: IElementProperty,
): boolean => {
  const typeA = getElementPropertyType(propA);
  const typeB = getElementPropertyType(propB);

  // Names must always match for any type
  if (propA.name !== propB.name || typeA !== typeB) {
    return false;
  }

  // ExpressionValues is objects and can't be compared with ===
  if (
    isElementExpressionProperty(propA) &&
    isElementExpressionProperty(propB)
  ) {
    return (
      propA.count?.expression === propB.count?.expression &&
      propA.unit === propB.unit
    );
  }
  // All other types can be compared with ===
  return propA.count === propB.count;
};

/**
 * Test if a multiple or a single property are members of a set of properties.
 * All properties must match to return true.
 * @param elementOrProps An element or an array of properties
 * @param properties One or more properties to test
 * @returns
 */
export const hasElementProperties = (
  elementOrProps: IPropertiesOrPropertyParent | undefined,
  ...properties: IElementProperty[]
): boolean => {
  return properties.every((p) => getExistingElementProperty(elementOrProps, p));
};

/**
 * Loop through a path of elements (in reverse order) to get the closest defined value for a element
 * @param path
 * @param name
 * @param defaultValue
 * @param ignoreZero
 * @returns
 */
export const getResolvedCountInPath = <D extends GetResolvedCountDefault>(
  path: ElementPath,
  name: ElementPropertyName,
  defaultValue: D,
  ignoreZero: boolean = true,
): GetResolvedCountReturnValue<D> => {
  // Content coverage is never inherited along the path — only the owning node counts.
  if (name === ElementPropertyName.ContentCoverage) {
    const owner = path.at(-1);
    return getElementPropertyResolvedCountByNameOrId(
      owner,
      name,
      defaultValue,
    ) as GetResolvedCountReturnValue<D>;
  }

  const value = findMap(path.toReversed(), (e) => {
    const resolved = getElementPropertyResolvedCountByNameOrId(e, name);
    return ignoreZero && resolved === 0 ? undefined : resolved;
  });
  // If no value was found, return the default value by calling the function with no
  return (value ??
    getElementPropertyResolvedCountByNameOrId(
      path[0],
      name,
      defaultValue,
    )) as GetResolvedCountReturnValue<D>;
};

/**
 * Get an existing property from a list of properties or element based on provided.
 * Similar means that the name and type must match.
 * When {@link compareSource} is false, matching by id ignores category/recipe (used when reusing definitions).
 * When true and the template has a category_id or recipe_id, same id only matches if that source id matches too.
 * @param elementOrProps An element or an array of properties
 * @param property The property to find
 * @param compareSource If true the category and recipe id must match
 * @returns
 */
export const getExistingElementProperty = (
  elementOrProps: IPropertiesOrPropertyParent | undefined,
  property: IElementProperty,
  compareSource: boolean = true,
): IElementProperty | undefined => {
  const elementProperties = getElementProperties(elementOrProps);

  const sourceId = getElementPropertySourceId(property);
  const type = getElementPropertyType(property);
  const { name, id } = property;
  return elementProperties.find((p) => {
    // Same id: align sources when comparing and the template carries a source id
    if (id && id === p.id) {
      if (!compareSource) return true;
      if (!isDefined(sourceId)) return true;
      return getElementPropertySourceId(p) === sourceId;
    }

    // If it's a select property, make sure options are equal
    if (
      isElementSelectProperty(property) &&
      isElementSelectProperty(p) &&
      !isSelectPropertyOptionsEqual(property.options, p.options)
    ) {
      return false;
    }

    const matchingSourceId = compareSource
      ? getElementPropertySourceId(p) === sourceId
      : true;

    // Source and name must match
    return matchingSourceId && p.type === type && p.name === name;
  });
};

/**
 * Check if two arrays of options are equal.
 * Can be either a list of [label: value] options or a key that can be used to get hardcoded options
 */
export const isSelectPropertyOptionsEqual = (
  optionsA: IElementPropertyOption[] | ElementPropertyOptionsKey,
  optionsB: IElementPropertyOption[] | ElementPropertyOptionsKey,
): boolean => {
  if (Array.isArray(optionsA) && Array.isArray(optionsB)) {
    return isEqual(
      optionsA.map((o) => o.value),
      optionsB.map((o) => o.value),
    );
  }
  return isEqual(optionsA, optionsB);
};

/**
 * Check if an array of properties is equal to another array of properties
 */
export const isElementPropertyListEqual = (
  propsA: IElementProperty[],
  propsB: IElementProperty[],
): boolean => {
  if (propsA?.length === propsB?.length) {
    return (
      propsA.every((pA) => {
        return propsB.some((pB) => isElementPropertiesEqual(pB, pA));
      }) &&
      propsB.every((pB) => {
        return propsA.some((pA) => isElementPropertiesEqual(pB, pA));
      })
    );
  }
  return false;
};

/**
 * The properties specific to a recipe or category properties
 * used to categorize a property
 */
type ElementPropertySourceProperties = Pick<
  IElementProperty,
  ElementPropertySource
>;

/**
 * Get the type of an element property
 * @param property
 * @returns
 */
export const getElementPropertySource = (
  property: ElementPropertySourceProperties,
): ElementPropertySource | undefined => {
  if (isElementPropertySourceCategory(property)) {
    return ElementPropertySource.Category;
  } else if (isElementPropertySourceRecipe(property)) {
    return ElementPropertySource.Recipe;
  }
  // Can this happen?
  return undefined;
};

export const getElementPropertySourceId = (
  property: ElementPropertySourceProperties,
): ElementPropertySourceID | undefined => {
  // make sure '' is not returned
  return property.category_id || property.recipe_id || undefined;
};

/**
 * Filter list of properties to pass back only those properties that should be added.
 * Properties not already existing in other words.
 * @param element
 * @param source
 * @param properties List of properties past to applyElementPropertiesOfType
 * @returns If returns undefined no changes should be made. If returns an empty array all properties of that type should be removed
 */
const getPropertiesToApply = (
  element: IElement,
  source: ElementPropertySource | undefined,
  properties?: IElementProperty[],
): IElementProperty[] | undefined => {
  if (properties === undefined) {
    return undefined;
  }
  if (properties.length === 0) {
    return !source ? undefined : properties;
  }

  // Fast path: skip UUID generation when existing properties already match
  const existingSourceProps = source
    ? getElementPropertiesBySource(element, source)
    : [];
  if (
    source &&
    existingSourceProps.length === properties.length &&
    hasElementProperties(element, ...properties)
  ) {
    return undefined;
  }

  const sourceId = getElementSourceId(element, source);

  properties = properties.map((p) =>
    applyIdAndSourceToProperty(
      validatePropertySource(element, p),
      source,
      sourceId,
    ),
  );

  // Re-check after applying source IDs
  return existingSourceProps.length === properties.length &&
    hasElementProperties(element, ...properties)
    ? undefined
    : properties;
};

/**
 * Options for {@link applyElementPropertiesOfSource}.
 *
 * `skipValidation` is set by enrich-pipeline callers (e.g.
 * {@link applyElementCategoryProperties}) to bypass strict validation on stored counts
 * that are no longer in the freshly-resolved options list (e.g. `form: Profile` after
 * material flips Aluminium → Steel). Validation runs at the user-input boundary instead.
 */
export type ApplyElementPropertiesOfSourceOptions = {
  skipValidation?: boolean;
};

/**
 * Apply properties to an element. If already existing type is category_id or recipe_id we should replace all properties of that type
 * @param element
 * @param source
 * @param props
 * @param options See {@link ApplyElementPropertiesOfSourceOptions}.
 * @returns
 */
export const applyElementPropertiesOfSource = (
  element: IElement,
  source: ElementPropertySource | undefined,
  props?: IElementProperty[],
  options: ApplyElementPropertiesOfSourceOptions = {},
): IElement => {
  const newProperties = getPropertiesToApply(element, source, props);

  // undefined should not change anything
  if (newProperties === undefined) {
    return element;
  }

  let properties: IElementProperty[] = getElementProperties(element).slice();

  // If not type just push the new properties
  if (!source) {
    return addElementProperties(element, ...newProperties);
  }

  // All properties that will be removed
  const removedProperties = properties.filter(
    (p) =>
      isElementPropertyOfSource(p, source) ||
      newProperties.some(
        (np) => !isElementPropertySourceCategory(np) && np.name === p.name,
      ),
  );

  // Remove them from properties array
  properties = properties.filter((p) => !removedProperties.includes(p));

  // Add all new properties
  properties.push(
    ...newProperties.map((p) => {
      // Reuse count from removed properties if possible
      const existing = getExistingProperty(removedProperties, p);

      return createElementProperty(
        {
          ...p,
          count: existing?.count ?? p.count,
          // Reuse ids only when swapping within the same element; catalog template ids are shared.
          id: existing?.id ?? createId(p, true),
        } as IElementProperty,
        undefined,
        false,
        { skipValidation: options.skipValidation },
      );
    }),
  );

  return {
    ...element,
    properties: options.skipValidation
      ? properties
      : validateElementProperties(properties),
  };
};

const getExistingProperty = (
  properties: IElementProperty[],
  newProperty: IElementProperty,
): IElementProperty | undefined => {
  // Find a property with the same name and type
  const reusable = properties.find(
    (p) => p.name === newProperty.name && p.type === newProperty.type,
  );

  // If it's a select property, make sure the reusable count is an option in the new property
  if (
    isElementSelectProperty(reusable) &&
    !getOptionByValue(newProperty, getCount(reusable, false))
  ) {
    return undefined;
  }

  // The count is not reusable, but the id is. Clearing count lets the caller
  // keep this property and take the destination count instead.
  if (
    reusable &&
    isExpressionCountOutsideDestinationBounds(reusable, newProperty)
  ) {
    return { ...reusable, count: undefined };
  }

  return reusable;
};

/**
 * Bounds {@link createElementProperty} will enforce for {@link property}.
 * Content coverage always uses the category min/max, because the factory injects
 * them even when the incoming recipe row does not.
 */
const destinationExpressionBounds = (
  property: IElementProperty,
): { min?: number; max?: number } | undefined => {
  if (
    property.name === ElementPropertyName.ContentCoverage &&
    contentCoverageProperty.type === ElementPropertyType.Expression
  ) {
    return {
      min: contentCoverageProperty.min,
      max: contentCoverageProperty.max,
    };
  }
  if (property.type !== ElementPropertyType.Expression) return undefined;
  if (!guards.is.number(property.min) && !guards.is.number(property.max)) {
    return undefined;
  }
  return { min: property.min, max: property.max };
};

/**
 * True when {@link existing}'s numeric count cannot be copied onto {@link destination}.
 * Non-numeric formulas are kept: a resolved `0` there is a placeholder until enrich solves it.
 *
 * @param existing - Property removed by the apply, candidate for count reuse
 * @param destination - Property definition the count would be copied onto
 */
const isExpressionCountOutsideDestinationBounds = (
  existing: IElementProperty | undefined,
  destination: IElementProperty,
): boolean => {
  if (!existing) return false;
  const bounds = destinationExpressionBounds(destination);
  if (!bounds) return false;
  const count = existing.count;
  if (!isExpressionValue(count) || !isNumeric(count.expression)) return false;
  if (guards.is.number(bounds.min) && count.resolved < bounds.min) return true;
  if (guards.is.number(bounds.max) && count.resolved > bounds.max) return true;
  return false;
};

const getOptionByValue = (
  property: IElementProperty,
  value?: IElementPropertyOption['value'],
): IElementPropertyOption | undefined => {
  return isElementSelectProperty(property)
    ? getSelectPropertyOptions(property).find((o) => o.value === value)
    : undefined;
};

export const isEditablePropertyName = (prop: IElementProperty): boolean =>
  isFullyEditableProperty(prop) && !isOneOf(ElementPropertyName, prop.name);

/**
 * If you can change the name or delete the property.
 * If the property belongs to the original element category, only the value can be modified.
 * @param prop
 * @returns true if the property can be deleted or renamed, false if only the value can be modified.
 */
export const isFullyEditableProperty = (prop: IElementProperty): boolean =>
  !isElementPropertySourceCategory(prop) &&
  !isElementQuantityName(prop.name) &&
  !prop.hidden &&
  !prop.readonly;

/**
 * Check if the property is a category property
 * @param prop
 * @returns
 */
export const isElementPropertySourceCategory = (
  prop: ElementPropertySourceProperties,
): boolean => isElementPropertyOfSource(prop, ElementPropertySource.Category);

/**
 * Check if the property is a recipe property
 * @param prop
 * @returns
 */
export const isElementPropertySourceRecipe = (
  prop: ElementPropertySourceProperties,
): boolean => isElementPropertyOfSource(prop, ElementPropertySource.Recipe);

/**
 * Check if the property is neither a category or recipe property
 * @param prop
 * @returns
 */
export const isElementPropertySourceNone = (
  prop: ElementPropertySourceProperties,
): boolean =>
  prop &&
  !isElementPropertySourceRecipe(prop) &&
  !isElementPropertySourceCategory(prop);

/**
 * Check if element is of a certain source (category, recipe or neither)
 * @param prop
 * @param source
 * @returns
 */
export const isElementPropertyOfSource = (
  prop: ElementPropertySourceProperties,
  source: ElementPropertySource | undefined,
): boolean => {
  if (!source) {
    return isElementPropertySourceNone(prop);
  }
  return prop && !!prop[source];
};

export const isIconElementProperty = (prop: IElementProperty): boolean => {
  return iconQuantityNames.includes(prop.name as IconQuantityName);
};

/**
 * Get all properties by a certain source
 * @param elementOrProps
 * @param source
 * @returns
 */
export const getElementPropertiesBySource = (
  elementOrProps: IPropertiesOrPropertyParent,
  source: ElementPropertySource | undefined,
): IElementProperty[] =>
  getElementProperties(elementOrProps).filter((p) =>
    isElementPropertyOfSource(p, source),
  );

/**
 * Get all properties from category source
 * @param elementOrProps
 * @returns
 */
export const getElementPropertiesByCategorySource = (
  elementOrProps: IPropertiesOrPropertyParent,
): IElementProperty[] =>
  getElementPropertiesBySource(elementOrProps, ElementPropertySource.Category);

/**
 * Get all properties from recipe source
 * @param elementOrProps
 * @returns
 */
export const getElementPropertiesByRecipeSource = (
  elementOrProps: IPropertiesOrPropertyParent,
): IElementProperty[] =>
  getElementPropertiesBySource(elementOrProps, ElementPropertySource.Recipe);

/**
 * Get all properties that are not of a certain source.
 * @param elementOrProps
 * @param source
 * @returns
 */
export const getElementPropertiesNotOfSource = (
  elementOrProps: IPropertiesOrPropertyParent,
  source: ElementPropertySource | undefined,
): IElementProperty[] =>
  getElementProperties(elementOrProps).filter(
    (p) => !isElementPropertyOfSource(p, source),
  );

/**
 * Get all element properties and default to empty array if it doesn't exist.
 * @param elementOrProps An element, category, recipe or an array of properties
 * @returns
 */
export const getElementProperties = <
  T extends IPropertiesOrPropertyParent | ElementWithProperties,
  R = T extends ElementQuantityRecord
    ? IElementQuantityProperty
    : IElementProperty,
>(
  elementOrProps:
    IPropertiesOrPropertyParent | ElementWithProperties | undefined,
): R[] => {
  if (!elementOrProps) {
    return EMPTY_PROPERTIES as R[];
  }
  if (Array.isArray(elementOrProps)) {
    return elementOrProps as R[];
  }
  if ('properties' in elementOrProps) {
    return (elementOrProps.properties ?? EMPTY_PROPERTIES) as R[];
  }
  if (isElementQuantityRecord(elementOrProps)) {
    return Object.values(elementOrProps) as R[];
  }
  return EMPTY_FROZEN_ARRAY as R[];
};

export const getElementAndQuantityProperties = (
  element?: OneOfElements,
): (IElementProperty | IElementQuantityExpressionProperty)[] => {
  const properties = getElementProperties(element);
  const quantityProperties = getElementQuantityProperties(element);
  return [...properties, ...quantityProperties];
};

/**
 * Get all element properties of an element and their children
 * @param root
 * @param includeQuantityProperties
 * @returns
 */
export const getAllElementProperties = <T extends boolean = false>(
  root: OneOfSearchableElements,
  includeQuantityProperties: T = false as T,
): T extends true
  ? (IElementProperty | IElementQuantityProperty)[]
  : IElementProperty[] => {
  const getProperties = includeQuantityProperties
    ? getElementAndQuantityProperties
    : getElementProperties;
  const nestedProperties: IElementProperty[] =
    flattenElements(root).flatMap(getProperties);

  const recipeRootProperites = isRecipe(root) ? getElementProperties(root) : [];
  return [...recipeRootProperites, ...nestedProperties] as T extends true
    ? (IElementProperty | IElementQuantityExpressionProperty)[]
    : IElementProperty[];
};

/**
 * Replace element properties in Element or array of properties
 * @param originalElementOrProperties
 * @param newProperties
 * @returns
 */
const replaceElementProperties = <T extends IPropertiesOrPropertyParent>(
  originalElementOrProperties: T,
  ...newProperties: IElementProperty[]
): T => {
  const properties = getElementProperties(originalElementOrProperties);
  let updatedProperties = properties;

  newProperties.forEach((p) => {
    updatedProperties = replaceById(updatedProperties, p.id, p);
  });

  // Nothing changed, don't modify anything
  if (shallowEqual(properties, updatedProperties)) {
    return originalElementOrProperties;
  }
  if (Array.isArray(originalElementOrProperties)) {
    return updatedProperties as T;
  }
  return { ...originalElementOrProperties, properties: updatedProperties };
};

/**
 * Get the id of either the recipe or the category.
 * Note that auto recipes are resolved to their original recipe id.
 * @param element
 * @param source
 * @returns
 */
export const getElementSourceId = (
  element: OneOfElements,
  source: ElementPropertySource | undefined,
): ElementPropertySourceID | undefined => {
  if (source === ElementPropertySource.Category) {
    return getElementCategoryId(element);
  }
  if (source === ElementPropertySource.Recipe) {
    return resolveAutoRecipeId(getRecipeId(element));
  }
  return undefined;
};

/**
 * If property has min or max, limit the resolved value.
 * @param property
 * @returns
 */
export const limitElementPropertyResolvedCount = <T extends IElementProperty>(
  property: T,
): T => {
  if (isElementExpressionProperty(property)) {
    const { min, max, count } = property;

    // Currently only limit count, not fallbackCount
    if (count) {
      const limited = clamp(count.resolved, min, max);

      return applyChanges(property, { count: { resolved: limited } });
    }
  }
  return property;
};

export const getUniquePropertyName = (
  element: OneOfElements,
  property: IElementProperty,
  newName: string,
): string | undefined => {
  const otherPropertyNames = getElementProperties(element)
    .filter((p) => p.id !== property.id)
    .map((p) => p.name);

  const propertyNames = [
    ...elementQuantityNames, // Make sure to not use the same name as a quantity
    ...otherPropertyNames,
  ];

  const convertedName = convertName(newName);

  return findFreeName(propertyNames, convertedName, {
    delimiter: '',
    startIndex: 1,
  });
};

const PROPERTY_COUNT_MAX_LENGTH = 25;
const PROPERTY_NAME_AND_COUNT_MAX_LENGTH = 35;

const nameAndCountTooLong = (property: IElementProperty): boolean => {
  return (
    typeof property.count === 'string' &&
    property.name.length + property.count.length >
      PROPERTY_NAME_AND_COUNT_MAX_LENGTH
  );
};

export const getCountTooltip = (property: IElementProperty): string => {
  if (typeof property.count !== 'string') {
    return '';
  }
  const countTooLong = property.count.length > PROPERTY_COUNT_MAX_LENGTH;

  return countTooLong && nameAndCountTooLong(property)
    ? labelFromValue(property.count)
    : '';
};

export const updateProperty = (
  project: Project,
  element: OneOfPropertyElements,
  property: IElementProperty | IElementQuantityProperty,
) => {
  const props = replaceById(
    getElementProperties(element),
    property.id,
    property,
  );

  property = limitElementPropertyResolvedCount(property);

  const updatedProject = updateElements(project, {
    id: element.id,
    properties: props,
  } as Partial<OneOfElementListElements> & IID);

  const updatedElement = getElementById(
    getBuilding(updatedProject),
    element.id,
  );

  const updatedProperty = getElementProperties(updatedElement)?.find(
    ({ id }) => id === property.id,
  );

  if (!updatedProperty) {
    throw new Error('Could not find updated property');
  }

  return isElement(updatedElement) ? updatedElement : undefined;
};
